import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AIChatWidget } from './AIChatWidget';
import { MockSSEProvider } from './__fixtures__/MockSSEProvider';
import { MockChatWidget } from './__fixtures__/MockChatWidget';
import { scenario as dataExtinguisherScenario } from './__fixtures__/scenarios/data-extinguisher-count';
import { scenario as officeOverdueScenario } from './__fixtures__/scenarios/office-overdue-inspections';
import { scenario as codeNfpaScenario } from './__fixtures__/scenarios/code-nfpa-lookup';
import { scenario as dataExportCodeScenario } from './__fixtures__/scenarios/data-export-with-code';
import { scenario as codeCitationDeepScenario } from './__fixtures__/scenarios/code-citation-deep';
import { scenario as officeBulkUpdateScenario } from './__fixtures__/scenarios/office-bulk-update';
import { scenario as dataLoadingTimeoutScenario } from './__fixtures__/scenarios/data-loading-timeout';
import { scenario as toolProgressActionsScenario } from './__fixtures__/scenarios/data-tool-progress-with-actions';
import { scenario as approvalFlowScenario } from './__fixtures__/scenarios/office-approval-flow';
import { scenario as generalActionsScenario } from './__fixtures__/scenarios/office-general-actions';
import { scenario as userAttachmentsScenario } from './__fixtures__/scenarios/user-attachments-scan';
import { scenario as longStreamingScenario } from './__fixtures__/scenarios/office-long-streaming';
import { scenario as deepLinkScenario } from './__fixtures__/scenarios/deep-link-navigation';
import { scenario as deflectionNoSupportScenario } from './__fixtures__/scenarios/deflection-no-support';
import { scenario as deflectionWithSupportScenario } from './__fixtures__/scenarios/deflection-with-support';
import { scenario as recordCardStatusScenario } from './__fixtures__/scenarios/record-card-status';
import { scenario as agentTransitionScenario } from './__fixtures__/scenarios/office-agent-transition';
import { scenario as conversationalFormScenario } from './__fixtures__/scenarios/office-conversational-form';
import { scenario as planApprovalFlowScenario } from './__fixtures__/scenarios/office-plan-approval-flow';
import { scenario as fieldDayBriefScenario } from './__fixtures__/scenarios/field-day-brief';
// Create a QueryClient instance for the real AIChatWidget stories
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: false,
            refetchOnWindowFocus: false
        }
    }
});
// ---------------------------------------------------------------------------
// Default stories (real AIChatWidget, no mock)
// ---------------------------------------------------------------------------
const meta = {
    title: 'Features/AIChatWidget/AIChatWidget',
    component: AIChatWidget,
    parameters: {
        layout: 'fullscreen'
    },
    decorators: [
        (Story) => (React.createElement(QueryClientProvider, { client: queryClient },
            React.createElement("div", { style: { height: '100vh', width: '100vw', padding: '20px', position: 'relative' } },
                React.createElement("div", { style: { marginBottom: '20px', padding: '16px', background: '#f5f5f5', borderRadius: '8px' } },
                    React.createElement("p", { style: { margin: 0, fontSize: '14px', color: '#666' } },
                        React.createElement("strong", null, "Note:"),
                        " Click the AI Assistant button in the bottom right corner to open the chat widget.")),
                React.createElement(Story, null))))
    ]
};
export default meta;
export const Default = {
    args: {
        userName: 'John Doe'
    }
};
export const WithoutUserName = {
    args: {
        userName: undefined
    }
};
export const DataAssistantInteraction = {
    render: (args) => (React.createElement(MockSSEProvider, null,
        React.createElement(MockChatWidget, { conversations: args.conversations, sseSequence: args.sseSequence, userMessage: args.userMessage, autoPlay: args.autoPlay, userName: args.userName }))),
    args: {
        conversations: dataExtinguisherScenario.conversations,
        sseSequence: dataExtinguisherScenario.sseSequence,
        userMessage: dataExtinguisherScenario.userMessage,
        autoPlay: false,
        userName: 'John Doe'
    },
    parameters: {
        docs: {
            description: {
                story: dataExtinguisherScenario.description
            }
        }
    }
};
export const DataAssistantAutoPlay = {
    render: (args) => (React.createElement(MockSSEProvider, null,
        React.createElement(MockChatWidget, { conversations: args.conversations, sseSequence: args.sseSequence, userMessage: args.userMessage, autoPlay: args.autoPlay, autoPlayDelayMs: args.autoPlayDelayMs, userName: args.userName }))),
    args: {
        conversations: dataExtinguisherScenario.conversations,
        sseSequence: dataExtinguisherScenario.sseSequence,
        userMessage: dataExtinguisherScenario.userMessage,
        autoPlay: true,
        autoPlayDelayMs: 1000,
        userName: 'John Doe'
    },
    parameters: {
        docs: {
            description: {
                story: 'Auto-play variant — the user message is sent automatically after 1 second.'
            }
        }
    }
};
export const OfficeAssistantInteraction = {
    render: (args) => (React.createElement(MockSSEProvider, null,
        React.createElement(MockChatWidget, { conversations: args.conversations, sseSequence: args.sseSequence, userMessage: args.userMessage, autoPlay: args.autoPlay, userName: args.userName }))),
    args: {
        conversations: officeOverdueScenario.conversations,
        sseSequence: officeOverdueScenario.sseSequence,
        userMessage: officeOverdueScenario.userMessage,
        autoPlay: false,
        userName: 'John Doe'
    },
    parameters: {
        docs: {
            description: {
                story: officeOverdueScenario.description
            }
        }
    }
};
export const CodeAssistantInteraction = {
    render: (args) => (React.createElement(MockSSEProvider, null,
        React.createElement(MockChatWidget, { conversations: args.conversations, sseSequence: args.sseSequence, userMessage: args.userMessage, autoPlay: args.autoPlay, userName: args.userName }))),
    args: {
        conversations: codeNfpaScenario.conversations,
        sseSequence: codeNfpaScenario.sseSequence,
        userMessage: codeNfpaScenario.userMessage,
        autoPlay: false,
        userName: 'John Doe'
    },
    parameters: {
        docs: {
            description: {
                story: codeNfpaScenario.description
            }
        }
    }
};
export const WithConversationHistory = {
    render: (args) => (React.createElement(MockSSEProvider, null,
        React.createElement(MockChatWidget, { conversations: args.conversations, sseSequence: args.sseSequence, userMessage: args.userMessage, autoPlay: args.autoPlay, userName: args.userName }))),
    args: {
        conversations: dataExtinguisherScenario.conversations,
        sseSequence: dataExtinguisherScenario.sseSequence,
        userMessage: dataExtinguisherScenario.userMessage,
        autoPlay: false,
        userName: 'John Doe'
    },
    parameters: {
        docs: {
            description: {
                story: 'Shows the widget with pre-existing conversation history in the drawer.'
            }
        }
    }
};
// ---------------------------------------------------------------------------
// Auto-play stories — New Block Renderers
// ---------------------------------------------------------------------------
export const DataExportWithCode = {
    render: (args) => (React.createElement(MockSSEProvider, null,
        React.createElement(MockChatWidget, { conversations: args.conversations, sseSequence: args.sseSequence, userMessage: args.userMessage, autoPlay: args.autoPlay, autoPlayDelayMs: args.autoPlayDelayMs, userName: args.userName }))),
    args: {
        conversations: dataExportCodeScenario.conversations,
        sseSequence: dataExportCodeScenario.sseSequence,
        userMessage: dataExportCodeScenario.userMessage,
        autoPlay: true,
        autoPlayDelayMs: 1000,
        userName: 'John Doe'
    },
    parameters: {
        docs: {
            description: {
                story: dataExportCodeScenario.description
            }
        }
    }
};
export const CodeCitationDeepDive = {
    render: (args) => (React.createElement(MockSSEProvider, null,
        React.createElement(MockChatWidget, { conversations: args.conversations, sseSequence: args.sseSequence, userMessage: args.userMessage, autoPlay: args.autoPlay, autoPlayDelayMs: args.autoPlayDelayMs, userName: args.userName }))),
    args: {
        conversations: codeCitationDeepScenario.conversations,
        sseSequence: codeCitationDeepScenario.sseSequence,
        userMessage: codeCitationDeepScenario.userMessage,
        autoPlay: true,
        autoPlayDelayMs: 1000,
        userName: 'John Doe'
    },
    parameters: {
        docs: {
            description: {
                story: codeCitationDeepScenario.description
            }
        }
    }
};
export const OfficeBulkUpdateResults = {
    render: (args) => (React.createElement(MockSSEProvider, null,
        React.createElement(MockChatWidget, { conversations: args.conversations, sseSequence: args.sseSequence, userMessage: args.userMessage, autoPlay: args.autoPlay, autoPlayDelayMs: args.autoPlayDelayMs, userName: args.userName }))),
    args: {
        conversations: officeBulkUpdateScenario.conversations,
        sseSequence: officeBulkUpdateScenario.sseSequence,
        userMessage: officeBulkUpdateScenario.userMessage,
        autoPlay: true,
        autoPlayDelayMs: 1000,
        userName: 'John Doe'
    },
    parameters: {
        docs: {
            description: {
                story: officeBulkUpdateScenario.description
            }
        }
    }
};
export const DataLoadingTimeout = {
    render: (args) => (React.createElement(MockSSEProvider, null,
        React.createElement(MockChatWidget, { conversations: args.conversations, sseSequence: args.sseSequence, userMessage: args.userMessage, autoPlay: args.autoPlay, autoPlayDelayMs: args.autoPlayDelayMs, userName: args.userName }))),
    args: {
        conversations: dataLoadingTimeoutScenario.conversations,
        sseSequence: dataLoadingTimeoutScenario.sseSequence,
        userMessage: dataLoadingTimeoutScenario.userMessage,
        autoPlay: true,
        autoPlayDelayMs: 1000,
        userName: 'John Doe'
    },
    parameters: {
        docs: {
            description: {
                story: dataLoadingTimeoutScenario.description
            }
        }
    }
};
export const ToolProgressWithActions = {
    render: (args) => (React.createElement(MockSSEProvider, null,
        React.createElement(MockChatWidget, { conversations: args.conversations, sseSequence: args.sseSequence, userMessage: args.userMessage, autoPlay: args.autoPlay, autoPlayDelayMs: args.autoPlayDelayMs, userName: args.userName }))),
    args: {
        conversations: toolProgressActionsScenario.conversations,
        sseSequence: toolProgressActionsScenario.sseSequence,
        userMessage: toolProgressActionsScenario.userMessage,
        autoPlay: true,
        autoPlayDelayMs: 1000,
        userName: 'John Doe'
    },
    parameters: {
        docs: {
            description: {
                story: toolProgressActionsScenario.description
            }
        }
    }
};
export const ApprovalFlow = {
    render: (args) => (React.createElement(MockSSEProvider, null,
        React.createElement(MockChatWidget, { conversations: args.conversations, sseSequence: args.sseSequence, userMessage: args.userMessage, autoPlay: args.autoPlay, autoPlayDelayMs: args.autoPlayDelayMs, userName: args.userName }))),
    args: {
        conversations: approvalFlowScenario.conversations,
        sseSequence: approvalFlowScenario.sseSequence,
        userMessage: approvalFlowScenario.userMessage,
        autoPlay: true,
        autoPlayDelayMs: 1000,
        userName: 'John Doe'
    },
    parameters: {
        docs: {
            description: {
                story: approvalFlowScenario.description
            }
        }
    }
};
export const GeneralActionsInteraction = {
    render: (args) => (React.createElement(MockSSEProvider, null,
        React.createElement(MockChatWidget, { conversations: args.conversations, sseSequence: args.sseSequence, userMessage: args.userMessage, autoPlay: args.autoPlay, autoPlayDelayMs: args.autoPlayDelayMs, userName: args.userName }))),
    args: {
        conversations: generalActionsScenario.conversations,
        sseSequence: generalActionsScenario.sseSequence,
        userMessage: generalActionsScenario.userMessage,
        autoPlay: true,
        autoPlayDelayMs: 1000,
        userName: 'John Doe'
    },
    parameters: {
        docs: {
            description: {
                story: generalActionsScenario.description
            }
        }
    }
};
// ---------------------------------------------------------------------------
// Phase I/K stories — User Attachments & Scan Status
// ---------------------------------------------------------------------------
export const UserAttachmentsScanStatus = {
    render: (args) => (React.createElement(MockSSEProvider, null,
        React.createElement(MockChatWidget, { conversations: args.conversations, sseSequence: args.sseSequence, userMessage: args.userMessage, autoPlay: args.autoPlay, autoPlayDelayMs: args.autoPlayDelayMs, userName: args.userName, preSeededMessages: userAttachmentsScenario.preSeededMessages }))),
    args: {
        conversations: userAttachmentsScenario.conversations,
        sseSequence: userAttachmentsScenario.sseSequence,
        userMessage: userAttachmentsScenario.userMessage,
        autoPlay: true,
        autoPlayDelayMs: 1000,
        userName: 'John Doe'
    },
    parameters: {
        docs: {
            description: {
                story: userAttachmentsScenario.description
            }
        }
    }
};
// ---------------------------------------------------------------------------
// Phase J story — Stream Cancellation (onStop) Test
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Phase M — SSE Reconnection UI stories
// ---------------------------------------------------------------------------
export const SSEReconnecting = {
    render: (args) => (React.createElement(MockSSEProvider, null,
        React.createElement(MockChatWidget, { conversations: args.conversations, sseSequence: args.sseSequence, userMessage: args.userMessage, autoPlay: false, userName: args.userName, connectionState: "reconnecting", reconnectAttempts: 3 }))),
    args: {
        conversations: dataExtinguisherScenario.conversations,
        sseSequence: dataExtinguisherScenario.sseSequence,
        userMessage: dataExtinguisherScenario.userMessage,
        userName: 'John Doe'
    },
    parameters: {
        docs: {
            description: {
                story: `Shows the reconnecting banner when the SSE connection drops and is retrying.
The banner displays "Reconnecting…" with the attempt count.`
            }
        }
    }
};
export const SSEConnectionError = {
    render: (args) => (React.createElement(MockSSEProvider, null,
        React.createElement(MockChatWidget, { conversations: args.conversations, sseSequence: args.sseSequence, userMessage: args.userMessage, autoPlay: false, userName: args.userName, connectionState: "error", reconnectAttempts: 6 }))),
    args: {
        conversations: dataExtinguisherScenario.conversations,
        sseSequence: dataExtinguisherScenario.sseSequence,
        userMessage: dataExtinguisherScenario.userMessage,
        userName: 'John Doe'
    },
    parameters: {
        docs: {
            description: {
                story: `Shows the error banner when max SSE reconnection retries have been exceeded.
The banner shows "Connection lost. Please refresh the page to reconnect."`
            }
        }
    }
};
export const LongStreamingOnStopTest = {
    render: (args) => (React.createElement(MockSSEProvider, null,
        React.createElement(MockChatWidget, { conversations: args.conversations, sseSequence: args.sseSequence, userMessage: args.userMessage, autoPlay: args.autoPlay, userName: args.userName }))),
    args: {
        conversations: longStreamingScenario.conversations,
        sseSequence: longStreamingScenario.sseSequence,
        userMessage: longStreamingScenario.userMessage,
        autoPlay: false,
        userName: 'John Doe'
    },
    parameters: {
        docs: {
            description: {
                story: `${longStreamingScenario.description}

**Testing Instructions:**
1. Open the chat widget
2. Send the message to trigger the long streaming response
3. Click the STOP button (square icon) while the response is streaming
4. Check the browser console for "cancelResponse" API call
5. Verify the partial response is saved and displayed
6. Confirm streaming stops immediately`
            }
        }
    }
};
// ---------------------------------------------------------------------------
// Phase N — Scenario Gap Closure stories
// ---------------------------------------------------------------------------
export const DeepLinkNavigation = {
    render: (args) => (React.createElement(MockSSEProvider, null,
        React.createElement(MockChatWidget, { conversations: args.conversations, sseSequence: args.sseSequence, userMessage: args.userMessage, autoPlay: args.autoPlay, userName: args.userName }))),
    args: {
        conversations: deepLinkScenario.conversations,
        sseSequence: deepLinkScenario.sseSequence,
        userMessage: deepLinkScenario.userMessage,
        autoPlay: false,
        userName: 'John Doe'
    },
    parameters: {
        docs: {
            description: {
                story: deepLinkScenario.description
            }
        }
    }
};
export const DeflectionNoSupport = {
    render: (args) => (React.createElement(MockSSEProvider, null,
        React.createElement(MockChatWidget, { conversations: args.conversations, sseSequence: args.sseSequence, userMessage: args.userMessage, autoPlay: args.autoPlay, userName: args.userName }))),
    args: {
        conversations: deflectionNoSupportScenario.conversations,
        sseSequence: deflectionNoSupportScenario.sseSequence,
        userMessage: deflectionNoSupportScenario.userMessage,
        autoPlay: false,
        userName: 'John Doe'
    },
    parameters: {
        docs: {
            description: {
                story: deflectionNoSupportScenario.description
            }
        }
    }
};
export const DeflectionWithSupport = {
    render: (args) => (React.createElement(MockSSEProvider, null,
        React.createElement(MockChatWidget, { conversations: args.conversations, sseSequence: args.sseSequence, userMessage: args.userMessage, autoPlay: args.autoPlay, userName: args.userName }))),
    args: {
        conversations: deflectionWithSupportScenario.conversations,
        sseSequence: deflectionWithSupportScenario.sseSequence,
        userMessage: deflectionWithSupportScenario.userMessage,
        autoPlay: false,
        userName: 'John Doe'
    },
    parameters: {
        docs: {
            description: {
                story: deflectionWithSupportScenario.description
            }
        }
    }
};
export const RecordCardStatus = {
    render: (args) => (React.createElement(MockSSEProvider, null,
        React.createElement(MockChatWidget, { conversations: args.conversations, sseSequence: args.sseSequence, userMessage: args.userMessage, autoPlay: args.autoPlay, userName: args.userName }))),
    args: {
        conversations: recordCardStatusScenario.conversations,
        sseSequence: recordCardStatusScenario.sseSequence,
        userMessage: recordCardStatusScenario.userMessage,
        autoPlay: false,
        userName: 'John Doe'
    },
    parameters: {
        docs: {
            description: {
                story: recordCardStatusScenario.description
            }
        }
    }
};
// ---------------------------------------------------------------------------
// Phase — Missing Block Renderers stories
// ---------------------------------------------------------------------------
export const AgentTransition = {
    render: (args) => (React.createElement(MockSSEProvider, null,
        React.createElement(MockChatWidget, { conversations: args.conversations, sseSequence: args.sseSequence, userMessage: args.userMessage, autoPlay: args.autoPlay, autoPlayDelayMs: args.autoPlayDelayMs, userName: args.userName }))),
    args: {
        conversations: agentTransitionScenario.conversations,
        sseSequence: agentTransitionScenario.sseSequence,
        userMessage: agentTransitionScenario.userMessage,
        autoPlay: true,
        autoPlayDelayMs: 1000,
        userName: 'John Doe'
    },
    parameters: {
        docs: {
            description: {
                story: agentTransitionScenario.description
            }
        }
    }
};
export const ConversationalForm = {
    render: (args) => (React.createElement(MockSSEProvider, null,
        React.createElement(MockChatWidget, { conversations: args.conversations, sseSequence: args.sseSequence, userMessage: args.userMessage, autoPlay: args.autoPlay, autoPlayDelayMs: args.autoPlayDelayMs, userName: args.userName }))),
    args: {
        conversations: conversationalFormScenario.conversations,
        sseSequence: conversationalFormScenario.sseSequence,
        userMessage: conversationalFormScenario.userMessage,
        autoPlay: true,
        autoPlayDelayMs: 1000,
        userName: 'John Doe'
    },
    parameters: {
        docs: {
            description: {
                story: conversationalFormScenario.description
            }
        }
    }
};
// ---------------------------------------------------------------------------
// Multi-Action Plan Approval story
// ---------------------------------------------------------------------------
export const PlanApprovalFlow = {
    render: (args) => (React.createElement(MockSSEProvider, null,
        React.createElement(MockChatWidget, { conversations: args.conversations, sseSequence: args.sseSequence, userMessage: args.userMessage, autoPlay: args.autoPlay, autoPlayDelayMs: args.autoPlayDelayMs, userName: args.userName }))),
    args: {
        conversations: planApprovalFlowScenario.conversations,
        sseSequence: planApprovalFlowScenario.sseSequence,
        userMessage: planApprovalFlowScenario.userMessage,
        autoPlay: true,
        autoPlayDelayMs: 1000,
        userName: 'John Doe'
    },
    parameters: {
        docs: {
            description: {
                story: planApprovalFlowScenario.description
            }
        }
    }
};
export const FieldDayBrief = {
    render: (args) => (React.createElement(MockSSEProvider, null,
        React.createElement(MockChatWidget, { conversations: args.conversations, sseSequence: args.sseSequence, userMessage: args.userMessage, autoPlay: args.autoPlay, autoPlayDelayMs: args.autoPlayDelayMs, userName: args.userName }))),
    args: {
        conversations: fieldDayBriefScenario.conversations,
        sseSequence: fieldDayBriefScenario.sseSequence,
        userMessage: fieldDayBriefScenario.userMessage,
        autoPlay: true,
        autoPlayDelayMs: 1000,
        userName: 'John Doe'
    },
    parameters: {
        docs: {
            description: {
                story: fieldDayBriefScenario.description
            }
        }
    }
};
